/**
 * receivingPhone.ts — turn a Meta `phone_number_id` into the per-row
 * "→ 99933 17711" badge AV staff need to know WHICH of our two numbers
 * a WhatsApp message landed on.
 *
 * Two senders since 2026-06-04 — both AV-owned, on the same webhook:
 *   - +91 99933 17711  (OneHash-liberated, primary lead-ack)  pni 926096937247472
 *   - +91 91793 03585  (AiSensy-liberated, new)               pni 1088402821017863
 *
 * Vivaswan's commit `8319bb1` propagates `receivingPhoneNumberId` from
 * `wa_inbound` → thread messages → communications → wa_conversations
 * index doc → workspace conversation row. This helper turns that field
 * into the badge model.
 *
 * Pure: no React, no Firebase — unit-tested directly.
 */

export type PhoneBadgeClass = 'liberated' | 'onehash' | 'other';

export interface PhoneBadge {
  /** Short human label, digit-grouped (e.g. "91793 03585"). */
  label: string;
  /** Full +91-prefixed phone for tooltips / accessibility. */
  fullPhone: string;
  /** CSS class suffix the renderer applies for cool/warm tinting. */
  classKey: PhoneBadgeClass;
}

/**
 * Known Meta phone-number-id → (full phone, class) for our two senders.
 * Both numbers are AV-owned post-liberation; the labels are deliberately
 * NOT branded copy ("liberated" / "onehash" are CSS hooks only — the
 * rendered text is just the digits).
 */
const KNOWN: Record<
  string,
  { label: string; fullPhone: string; classKey: PhoneBadgeClass }
> = {
  // OneHash-liberated, primary lead-ack number.
  '926096937247472': {
    label: '99933 17711',
    fullPhone: '+91 99933 17711',
    classKey: 'onehash',
  },
  // AiSensy-liberated, secondary.
  '1088402821017863': {
    label: '91793 03585',
    fullPhone: '+91 91793 03585',
    classKey: 'liberated',
  },
};

/**
 * Config-driven ROLE labels for each of our receiving numbers, used by the
 * WhatsApp-Inbox "Showing:" number dropdown. The role name (Sales / Client
 * Care) is a BUSINESS label distinct from the badge digits — keep it here so
 * a rename touches one place only. `label` is the human role; `line` is the
 * full +91 number (derived once here, in sync with the KNOWN map above).
 *
 * Order is the order the dropdown lists them. A number absent from this map
 * still works (it falls back to its digit label in the dropdown), but giving
 * it a role here is the supported path.
 */
export interface ReceivingNumberRole {
  /** Meta phone-number-id this role maps to. */
  phoneId: string;
  /** Human role label shown in the dropdown (e.g. "Sales"). */
  label: string;
  /** Full +91-prefixed number shown as the dropdown sub-line. */
  line: string;
}

export const RECEIVING_NUMBER_ROLES: ReceivingNumberRole[] = [
  {
    phoneId: '926096937247472', // OneHash-liberated, +91 99933 17711
    label: 'Sales',
    line: '+91 99933 17711',
  },
  {
    phoneId: '1088402821017863', // AiSensy-liberated, +91 91793 03585
    label: 'Client Care',
    line: '+91 91793 03585',
  },
];

/** Lookup the role config for a phone-number-id (null when not configured). */
export function roleForReceivingPhone(
  receivingPhoneNumberId: string | null | undefined,
): ReceivingNumberRole | null {
  const id = String(receivingPhoneNumberId || '').trim();
  if (!id) return null;
  return RECEIVING_NUMBER_ROLES.find((r) => r.phoneId === id) || null;
}

/** Last-resort label for an unknown phone-number-id — group from the right
 *  in 5-digit chunks so a long Meta id is at least scannable. */
function fallbackLabel(id: string): string {
  const s = String(id || '').trim();
  if (!s) return '';
  // Show the last 10 chars grouped, so unknown ids still look like a
  // recognisable phone-ish handle in the chip.
  const tail = s.length > 10 ? s.slice(-10) : s;
  return tail.replace(/(\d{5})(?=\d)/g, '$1 ');
}

/**
 * Resolve a Meta `phone_number_id` to the badge model. Returns `null`
 * when there is no input — legacy messages from before commit `8319bb1`
 * carry no `receivingPhoneNumberId`, and the renderer must show NO
 * badge in that case (never "→ undefined").
 */
export function formatReceivingPhoneBadge(
  receivingPhoneNumberId: string | null | undefined,
): PhoneBadge | null {
  const id = String(receivingPhoneNumberId || '').trim();
  if (!id) return null;
  const known = KNOWN[id];
  if (known) return { ...known };
  return {
    label: fallbackLabel(id),
    fullPhone: id,
    classKey: 'other',
  };
}
